import React, { useState, useEffect, useCallback } from 'react';
import { useStages, stageLabel } from '@/lib/stages';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, ChevronLeft, ChevronRight, ClipboardCheck, ArrowUpDown, ArrowUp, ArrowDown, AlertCircle, Trash2, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { statusPillClasses } from '@/lib/status';
import DriveByModal from '@/components/DriveByModal';
import Combobox from '@/components/Combobox';
import MarkupPicker from '@/components/MarkupPicker';
import DiscountPicker from '@/components/DiscountPicker';
import { FindClientOrPropertyDialog } from '@/components/FindClientOrProperty';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import IdChip from '@/components/IdChip';
import { toast } from 'sonner';

export default function AppraisalsPage() {
  const { api } = useAuth();
  const stages = useStages(api);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [appraisals, setAppraisals] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  // Audit deep-links land here via `?filter=no-quote-type|no-roof-details`.
  // We map them onto the existing `smart_filter` mechanism (which the
  // backend already understands) and synthesise a virtual "audit chip"
  // for the count strip so the operator sees what they're filtered to.
  const auditFilter = searchParams.get('filter') || '';
  const auditToSmart = (af) => ({
    'no-quote-type': 'no_quote_type',
    'no-roof-details': 'no_roof_details',
  })[af] || '';
  const [smartFilter, setSmartFilter] = useState(
    searchParams.get('smart') || auditToSmart(auditFilter) || ''
  );
  const [filterCounts, setFilterCounts] = useState(null);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [showDriveBy, setShowDriveBy] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [findFreeOpen, setFindFreeOpen] = useState(false);
  // Bulk-selection model: selectedIds is the set of checked appraisal
  // _ids. anchorId tracks the last-clicked checkbox so shift-clicking
  // the next one can select the entire visible range between them.
  // We deliberately store a Set so toggle ops are O(1) and the chrome
  // ("N selected" / bulk bar) re-renders on every change.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [anchorId, setAnchorId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkQuoteType, setBulkQuoteType] = useState('');
  const [allQuoteTypes, setAllQuoteTypes] = useState([]);
  const [bulkResultFlash, setBulkResultFlash] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter177 — soft-delete view modes + delete dialog target.
  const [viewMode, setViewMode] = useState('live');
  const [delTarget, setDelTarget] = useState(null);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
    setPage(1);
  };
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} className="text-slate-300" />;
    return sortDir === 'asc' ? <ArrowUp size={12} className="text-[#D35400]" /> : <ArrowDown size={12} className="text-[#D35400]" />;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const clientId = searchParams.get('client_id') || '';
      const sf = smartFilter ? `&smart_filter=${smartFilter}` : '';
      const vm = viewMode === 'all' ? '&include_deleted=true' : viewMode === 'deleted' ? '&only_deleted=true' : '';
      const { data } = await api.get(`/appraisals?page=${page}&limit=${limit}&search=${search}&status=${statusFilter}&client_id=${clientId}&sort_by=${sortBy}&sort_dir=${sortDir}${sf}${vm}`);
      setAppraisals(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, page, limit, search, statusFilter, smartFilter, sortBy, sortDir, searchParams, viewMode]);

  useEffect(() => { load(); }, [load]);

  const restoreAppraisal = async (a) => {
    try {
      await api.post(`/appraisals/${a._id}/restore`);
      toast.success('Appraisal restored');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  // Load smart-filter counts once on mount + after any successful save
  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get('/appraisals/smart-filter-counts');
      setFilterCounts(data);
    } catch (e) { console.error(e); }
  }, [api]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Quote Types list — loaded once for the bulk-edit dropdown.
  useEffect(() => {
    let cancelled = false;
    api.get('/quote-types')
      .then(({ data }) => { if (!cancelled) setAllQuoteTypes((data || []).map(t => t.ref).sort()); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api]);

  // Clear selection when filters / page change — keeps the "N
  // selected" count from referencing rows that are no longer visible
  // (which would silently apply edits to invisible appraisals if the
  // operator clicked Apply without realising).
  useEffect(() => {
    setSelectedIds(new Set());
    setAnchorId(null);
  }, [smartFilter, statusFilter, page, search]);

  // Toggle a row's checked state. Shift+click selects the range
  // between this row and the anchor (last-clicked) row in the
  // current visible list, so the operator can select 30 rows in one
  // action.
  const toggleRow = useCallback((id, shiftKey) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shiftKey && anchorId && anchorId !== id) {
        const idx = appraisals.findIndex(a => a._id === id);
        const aIdx = appraisals.findIndex(a => a._id === anchorId);
        if (idx > -1 && aIdx > -1) {
          const [lo, hi] = idx < aIdx ? [idx, aIdx] : [aIdx, idx];
          // Range-add (never range-remove) so shift-click can't
          // accidentally wipe a curated selection.
          for (let i = lo; i <= hi; i++) next.add(appraisals[i]._id);
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setAnchorId(id);
  }, [appraisals, anchorId]);

  const allOnPageSelected = appraisals.length > 0 && appraisals.every(a => selectedIds.has(a._id));
  const someOnPageSelected = appraisals.some(a => selectedIds.has(a._id));
  const togglePage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        appraisals.forEach(a => next.delete(a._id));
      } else {
        appraisals.forEach(a => next.add(a._id));
      }
      return next;
    });
    setAnchorId(null);
  }, [appraisals, allOnPageSelected]);

  const clearSelection = useCallback(() => { setSelectedIds(new Set()); setAnchorId(null); }, []);

  const applyBulkQuoteType = useCallback(async () => {
    if (!bulkQuoteType || selectedIds.size === 0) return;
    if (!window.confirm(`Apply Quote Type "${bulkQuoteType}" to ${selectedIds.size} appraisal${selectedIds.size === 1 ? '' : 's'}?\n\nManual rows on the affected appraisals are not changed; only the appraisal.quote_type_ref field is set.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/appraisals/bulk-update', {
        appraisal_ids: [...selectedIds],
        updates: { quote_type_ref: bulkQuoteType },
      });
      setBulkResultFlash({ kind: 'ok', text: `Updated ${data.modified}/${data.matched} appraisals → "${bulkQuoteType}"` });
      setTimeout(() => setBulkResultFlash(null), 5000);
      clearSelection();
      await load();
      await loadCounts();
    } catch (e) {
      setBulkResultFlash({ kind: 'err', text: e?.response?.data?.detail || 'Bulk update failed' });
    } finally {
      setBulkBusy(false);
    }
  }, [api, bulkQuoteType, selectedIds, clearSelection, load, loadCounts]);


  // Quick views = runner stages (labels from the designer) + follow-up views.
  const STAGE_TONES = { client_data: 'orange', appraisal_booked: 'orange', quote_sent: 'amber', accepted: 'blue', declined: 'slate',
    work_booked: 'teal', work_in_progress: 'teal', completed: 'emerald', invoiced: 'teal', overdue: 'rose', paid: 'emerald' };
  const stageChips = stages.filter(st => STAGE_TONES[st.key]).map(st => ({ key: st.key, label: st.label, tone: STAGE_TONES[st.key], urgent: st.key === 'overdue' }));
  const ins = (arr, afterKey, chip) => { const i = arr.findIndex(c => c.key === afterKey); return i < 0 ? [...arr, chip] : [...arr.slice(0, i + 1), chip, ...arr.slice(i + 1)]; };
  let chips = ins(stageChips, 'quote_sent', { key: 'follow_up_due', label: 'Follow-up due', tone: 'rose', urgent: true });
  chips = ins(chips, 'invoiced', { key: 'invoiced_overdue_30d', label: 'Invoiced 30d+', tone: 'rose', urgent: true });
  const SMART_CHIPS = [
    { key: '', label: 'All', tone: 'slate' },
    ...chips,
    { key: 'closed', label: 'Closed', tone: 'slate' },
    { key: 'drive_by', label: 'Drive-by', tone: 'purple' },
    { key: 'legacy', label: 'Legacy', tone: 'slate' },
    // Audit-driven filters — only surfaced as a chip when an audit
    // deep-link has set them. The lookup-by-key in the count strip
    // ("X appraisals") still works without exposing them in the chip
    // bar above.
    { key: 'no_quote_type', label: 'Missing Quote Type', tone: 'rose', audit: true },
    { key: 'no_roof_details', label: 'Missing Roof Details', tone: 'rose', audit: true },
  ];
  const TONE_CLS = {
    slate: 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50',
    orange: 'border-orange-200 text-orange-700 hover:bg-orange-50',
    amber: 'border-amber-200 text-amber-700 hover:bg-amber-50',
    rose: 'border-rose-200 text-rose-700 hover:bg-rose-50',
    blue: 'border-blue-200 text-blue-700 hover:bg-blue-50',
    teal: 'border-teal-200 text-teal-700 hover:bg-teal-50',
    emerald: 'border-emerald-200 text-emerald-700 hover:bg-emerald-50',
    purple: 'border-purple-200 text-purple-700 hover:bg-purple-50',
  };
  const TONE_ACTIVE = {
    slate: 'bg-slate-700 text-white border-slate-700',
    orange: 'bg-orange-600 text-white border-orange-600',
    amber: 'bg-amber-500 text-white border-amber-500',
    rose: 'bg-rose-600 text-white border-rose-600',
    blue: 'bg-blue-600 text-white border-blue-600',
    teal: 'bg-teal-600 text-white border-teal-600',
    emerald: 'bg-emerald-600 text-white border-emerald-600',
    purple: 'bg-purple-600 text-white border-purple-600',
  };


  const statuses = ['', ...stages.map(st => st.value)];

  return (
    <div data-testid="appraisals-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Appraisals</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium"><span className="font-mono-num font-bold text-slate-700">{total.toLocaleString()}</span> {smartFilter ? `${SMART_CHIPS.find(c => c.key === smartFilter)?.label?.toLowerCase()} appraisals` : 'total appraisals'}{smartFilter && <button onClick={() => { setSmartFilter(''); setPage(1); }} className="ml-2 text-xs text-[#D35400] hover:underline font-semibold">Clear filter</button>}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* Iter177 — view-mode picker. Live = filter out
              soft-deleted; Incl. deleted = both; Deleted = only
              soft-deleted (with Restore CTA per row). */}
          <div data-testid="appraisals-view-mode" className="flex items-center gap-1 border border-[#E2E2DF] rounded-md p-0.5 bg-slate-50/50 text-xs">
            {[
              { k: 'live',    label: 'Live',          icon: <Eye size={11} /> },
              { k: 'all',     label: 'Incl. deleted', icon: <EyeOff size={11} /> },
              { k: 'deleted', label: 'Deleted',       icon: <Trash2 size={11} /> },
            ].map(opt => (
              <button
                key={opt.k}
                data-testid={`appraisals-view-${opt.k}`}
                onClick={() => { setViewMode(opt.k); setPage(1); }}
                className={`flex items-center gap-1 px-2.5 py-1 rounded font-semibold transition ${viewMode === opt.k ? 'bg-white text-[#D35400] shadow-sm border border-[#D35400]/30' : 'text-slate-500 hover:text-slate-800'}`}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>
          <button data-testid="add-driveby-button" onClick={() => setShowDriveBy(true)} className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-purple-500/20">
            <Plus size={16} strokeWidth={2.5} /> Drive-by
          </button>
          <button data-testid="add-free-appraisal-button" onClick={() => setFindFreeOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-md text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-sky-500/20">
            <Plus size={16} strokeWidth={2.5} /> Free Appraisal
          </button>
          <button data-testid="add-appraisal-find-btn" onClick={() => setFindOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold transition-colors shadow-sm">
            <Plus size={16} strokeWidth={2.5} /> New Appraisal
          </button>
        </div>
      </div>

      {/* Audit-deep-link banner — shows when an audit "Jump to fix"
          link landed us here. Makes the destructive/clean-up intent
          obvious and gives a single "show all" exit. */}
      {smartFilter === 'no_quote_type' || smartFilter === 'no_roof_details' ? (
        <div data-testid="audit-deeplink-banner" className="flex items-center gap-2 px-3 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-900 text-xs">
          <AlertCircle size={14} className="text-rose-600" />
          <span className="font-semibold">Showing appraisals flagged by the Data Linkage audit: {smartFilter === 'no_quote_type' ? 'Missing Quote Type' : 'Missing Roof Details'}.</span>
          <span className="italic text-rose-700/80">Open each one → fill in the missing info → audit clears automatically.</span>
          <button
            onClick={() => { setSmartFilter(''); setPage(1); }}
            data-testid="audit-deeplink-clear"
            className="ml-auto px-2 py-0.5 rounded bg-white text-rose-700 ring-1 ring-rose-300 font-semibold hover:bg-rose-100"
          >Show all</button>
        </div>
      ) : null}

      {/* Smart-filter chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 mr-1">Quick views:</span>
        {SMART_CHIPS.filter(chip => !chip.audit).map(chip => {
          const active = smartFilter === chip.key;
          const cnt = filterCounts ? (filterCounts[chip.key || 'all']) : null;
          const cls = active ? TONE_ACTIVE[chip.tone] : TONE_CLS[chip.tone];
          return (
            <button
              key={chip.key || 'all'}
              data-testid={`chip-${chip.key || 'all'}`}
              onClick={() => { setSmartFilter(chip.key); setPage(1); }}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-semibold transition-all ${cls}`}
            >
              {chip.urgent && !active && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />}
              {chip.label}
              {cnt !== null && cnt !== undefined && (
                <span className={`text-[10px] font-mono-num ${active ? 'text-white/85' : 'text-slate-400'}`}>{cnt.toLocaleString()}</span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="appraisal-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by quote # or text…" className="w-full pl-10 pr-4 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
        </div>
        <div className="relative">
          <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <select data-testid="appraisal-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="pl-10 pr-8 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400] appearance-none">
            <option value="">All Statuses</option>
            {statuses.filter(Boolean).map(s => <option key={s} value={s}>{stages.find(st => st.value === s)?.label || s}</option>)}
          </select>
        </div>
      </div>
      {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> : (
        <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                {/* Bulk-select header checkbox — toggles every visible
                    row. Triple-state (checked / indeterminate / empty)
                    so the operator can see if some rows on this page
                    are already selected from a previous page click. */}
                <th className="w-[40px] px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    data-testid="appraisals-bulk-toggle-page"
                    checked={allOnPageSelected}
                    ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                    onChange={togglePage}
                    onClick={(e) => e.stopPropagation()}
                    title={allOnPageSelected ? 'Deselect this page' : 'Select all visible rows'}
                    className="cursor-pointer"
                  />
                </th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer select-none hover:text-[#D35400] w-[88px]" onClick={() => toggleSort('appraisal_no')}><span className="flex items-center gap-1">Ref # <SortIcon field="appraisal_no" /></span></th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer select-none hover:text-[#D35400]" onClick={() => toggleSort('created_at')}><span className="flex items-center gap-1">Client <SortIcon field="created_at" /></span></th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell cursor-pointer select-none hover:text-[#D35400]" onClick={() => toggleSort('quote_type_ref')}><span className="flex items-center gap-1">Quote Type <SortIcon field="quote_type_ref" /></span></th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Property</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer select-none hover:text-[#D35400]" onClick={() => toggleSort('appraisal_status')}><span className="flex items-center gap-1">Status <SortIcon field="appraisal_status" /></span></th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell cursor-pointer select-none hover:text-[#D35400]" onClick={() => toggleSort('contract_amount')}><span className="flex items-center gap-1 justify-end">Value <SortIcon field="contract_amount" /></span></th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell cursor-pointer select-none hover:text-[#D35400]" onClick={() => toggleSort('calc_rtr_profit')}><span className="flex items-center gap-1 justify-end">RTR Profit <SortIcon field="calc_rtr_profit" /></span></th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-[80px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {appraisals.map(a => {
                const isSelected = selectedIds.has(a._id);
                return (
                <tr
                  key={a._id}
                  data-testid={`appraisal-row-${a._id}`}
                  className={`border-t border-[#E2E2DF] cursor-pointer transition-colors group ${
                    isSelected
                      ? 'bg-amber-50/80 hover:bg-amber-100/80 ring-1 ring-inset ring-amber-300'
                      : a.is_deleted
                        ? 'bg-rose-50/40 hover:bg-rose-50/70'
                        : 'hover:bg-orange-50/30'
                  }`}
                  onClick={() => navigate(`/appraisals/${a._id}`)}
                >
                  {/* Bulk-select cell. Shift-click range-extends from
                      the last-clicked checkbox so the operator can
                      tag 30 rows in one motion. Stop propagation so
                      the row's onClick (navigate) doesn't fire. */}
                  <td className="w-[40px] px-3 py-3 text-center relative">
                    {isSelected && <div className="absolute inset-y-0 left-0 w-[3px] bg-amber-500" />}
                    <input
                      type="checkbox"
                      data-testid={`appraisal-select-${a._id}`}
                      checked={isSelected}
                      onChange={() => {}}
                      onClick={(e) => { e.stopPropagation(); toggleRow(a._id, e.shiftKey); }}
                      className="cursor-pointer"
                      title="Shift-click to select a range"
                    />
                  </td>
                  <td className="px-3 py-3 text-sm tabular-nums">
                    <div className="flex items-center gap-1.5">
                      {a.appraisal_no ? <IdChip type="appraisal" value={a.appraisal_no} size="md" testId={`appraisal-list-chip-${a._id}`} /> : <span className="text-slate-300">—</span>}
                      {a.is_deleted && <span data-testid={`appraisal-deleted-badge-${a._id}`} className="text-[8px] font-bold uppercase tracking-wider text-rose-700 bg-rose-100 border border-rose-300 rounded-full px-1.5 py-0">Del</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 relative">
                    <div className="absolute inset-y-0 left-0 w-[2px] bg-[#D35400] opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#D35400]/15 to-[#D35400]/5 flex items-center justify-center flex-shrink-0 ring-1 ring-[#D35400]/10">
                        <ClipboardCheck size={15} className="text-[#D35400]" strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{a.client_name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500 font-mono-num">{a.appraisal_date ? new Date(a.appraisal_date).toLocaleDateString('en-NZ') : '—'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-sm text-slate-600">{a.quotation_type || a.quote_type_ref || '-'}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-sm text-slate-600">{a.property_address || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${statusPillClasses(a.appraisal_status)}`}>{a.appraisal_status === 'legacy_pending_review' ? 'Legacy' : (stageLabel(stages, a.appraisal_status) || 'New')}</span>
                  </td>
                  <td className="px-4 py-3 text-right hidden md:table-cell text-sm font-medium text-slate-700 font-mono-num">{(() => {
                    const v = a.contract_amount > 0 ? a.contract_amount : a.calc_quote_value;
                    const isCalc = !(a.contract_amount > 0) && a.calc_quote_value > 0;
                    if (!v || v <= 0) return '-';
                    return <span className={isCalc ? 'text-slate-500 italic' : ''} title={isCalc ? 'Calculated from components' : 'Contract amount'}>${Number(v).toLocaleString()}</span>;
                  })()}</td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell text-sm font-medium font-mono-num">{a.calc_rtr_profit > 0 ? <span className="text-green-700 font-semibold">${Number(a.calc_rtr_profit).toLocaleString()}</span> : <span className="text-slate-300">-</span>}</td>
                  <td className="px-3 py-3 text-right">
                    {a.is_deleted ? (
                      <button
                        data-testid={`restore-appraisal-${a._id}`}
                        onClick={(e) => { e.stopPropagation(); restoreAppraisal(a); }}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 border border-emerald-300 px-2 py-1 rounded hover:bg-emerald-50"
                      >
                        <RotateCcw size={11} /> Restore
                      </button>
                    ) : (
                      <button
                        data-testid={`delete-appraisal-row-${a._id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDelTarget({
                            kind: 'appraisal',
                            id: a._id,
                            label: a.appraisal_no ? `A${a.appraisal_no}` : `${a.quotation_type || a.quote_type_ref || 'Appraisal'} ${a._id.slice(-6)}`,
                          });
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 border border-rose-200 px-2 py-1 rounded hover:bg-rose-50"
                        title="Delete appraisal (soft or hard)"
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
              {appraisals.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">No appraisals found</td></tr>}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E2DF]">
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Showing {((page-1)*limit)+1}-{Math.min(page*limit, total)} of {total}</span>
              <select data-testid="appraisals-per-page" value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className="text-xs border border-[#E2E2DF] rounded px-2 py-1 bg-white">
                <option value={25}>25/page</option>
                <option value={50}>50/page</option>
                <option value={100}>100/page</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Page {page}/{pages}</span>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 rounded border border-[#E2E2DF] hover:bg-slate-50 disabled:opacity-30"><ChevronLeft size={16} /></button>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1.5 rounded border border-[#E2E2DF] hover:bg-slate-50 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        </div>
      )}

      <FindClientOrPropertyDialog open={findOpen} onClose={() => setFindOpen(false)} mode="go" />
      <FindClientOrPropertyDialog open={findFreeOpen} onClose={() => setFindFreeOpen(false)} mode="free" />

      {showDriveBy && (
        <DriveByModal
          api={api}
          onClose={() => setShowDriveBy(false)}
          onCreated={(ap) => { setShowDriveBy(false); loadCounts(); navigate(`/appraisals/${ap._id}`); }}
        />
      )}

      {/* Sticky bulk-action bar — appears whenever ≥1 row is selected.
          Sits above the bottom edge with a subtle elevation so it's
          unmissable but doesn't fight the page chrome. */}
      {selectedIds.size > 0 && (
        <div
          data-testid="appraisals-bulk-bar"
          className="fixed left-1/2 -translate-x-1/2 bottom-6 z-40 bg-[#0F172A] text-white rounded-lg shadow-2xl ring-1 ring-slate-700 px-4 py-3 flex items-center gap-3 flex-wrap max-w-[95vw]"
        >
          <span className="text-xs uppercase tracking-wide font-bold text-amber-300">{selectedIds.size} selected</span>
          <span className="text-[10px] text-slate-300 italic hidden sm:inline">Shift-click checkboxes to range-select.</span>
          <div className="h-5 w-px bg-slate-600" />
          <select
            data-testid="appraisals-bulk-quote-type"
            value={bulkQuoteType}
            onChange={e => setBulkQuoteType(e.target.value)}
            className="text-xs px-2 py-1.5 rounded bg-slate-800 text-white ring-1 ring-slate-600 hover:bg-slate-700 min-w-[200px]"
          >
            <option value="">— Apply Quote Type… —</option>
            {allQuoteTypes.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
          <button
            data-testid="appraisals-bulk-apply"
            disabled={!bulkQuoteType || bulkBusy}
            onClick={applyBulkQuoteType}
            className="text-xs px-3 py-1.5 rounded bg-[#D35400] hover:bg-[#A04000] disabled:opacity-40 disabled:cursor-not-allowed font-semibold inline-flex items-center gap-1"
          >{bulkBusy ? 'Applying…' : 'Apply'}</button>
          <div className="h-5 w-px bg-slate-600" />
          <button
            data-testid="appraisals-bulk-clear"
            onClick={clearSelection}
            className="text-xs px-2 py-1 rounded text-slate-300 hover:text-white hover:bg-slate-700 font-semibold"
          >Clear</button>
        </div>
      )}

      {bulkResultFlash && (
        <div
          data-testid="appraisals-bulk-flash"
          className={`fixed left-1/2 -translate-x-1/2 bottom-24 z-50 px-4 py-2 rounded-md shadow-lg text-xs font-semibold ring-1 ${
            bulkResultFlash.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
              : 'bg-rose-50 text-rose-800 ring-rose-200'
          }`}
        >{bulkResultFlash.text}</div>
      )}

      {/* Iter177 — universal Soft/Hard Delete modal for the list-page
          row delete affordance. */}
      {delTarget && (
        <DeleteConfirmDialog
          kind={delTarget.kind}
          entityId={delTarget.id}
          label={delTarget.label}
          api={api}
          onClose={() => setDelTarget(null)}
          onDeleted={() => { setDelTarget(null); load(); loadCounts(); }}
        />
      )}
    </div>
  );
}
